import { useState, useCallback, useEffect, useMemo } from 'react';
import { Loader2, Sparkles, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { onboardingService } from '@/services/onboardingService';
import { recoveryApi } from '@/services/recoveryApi';
import { recoveryKeys } from '@/hooks/useRecoveryStatus';
import { QuestionCard } from '@/features/onboarding/components/QuestionCard';
import type { SurveyQuestion } from '@/types/onboarding';

interface RecoverySurveyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  /** If survey data already exists, skip to generation. */
  existingSessionId?: string | null;
  /** Source page for analytics. */
  source?: string;
  /** Question IDs to show (from backend recovery status). Falls back to all non-banner questions. */
  requiredQuestionIds?: string[];
}

type ModalState = 'SURVEY' | 'GENERATING' | 'COMPLETE' | 'ERROR';

/**
 * Lightweight recovery survey modal.
 * Shows only the survey questions (not full onboarding) in a dialog.
 * On submit: creates recovery session -> submits responses -> triggers regeneration.
 */
export function RecoverySurveyModal({
  open,
  onOpenChange,
  userId,
  existingSessionId,
  source = 'PROFILE_PAGE',
  requiredQuestionIds,
}: RecoverySurveyModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [state, setState] = useState<ModalState>(existingSessionId ? 'GENERATING' : 'SURVEY');
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setCorrelationId] = useState<string | null>(null);

  // Show all interactive questions (skip display-only HEADING/BANNER types).
  // requiredQuestionIds from backend can optionally restrict the set.
  const questionFilter = useMemo(() => {
    if (requiredQuestionIds && requiredQuestionIds.length > 0) {
      const idSet = new Set(requiredQuestionIds);
      return (q: SurveyQuestion) => idSet.has(q.id);
    }
    return (q: SurveyQuestion) => q.type !== 'HEADING' && q.type !== 'BANNER';
  }, [requiredQuestionIds]);

  const loadSurvey = useCallback(async () => {
    try {
      setIsLoading(true);
      const survey = await onboardingService.fetchSurvey();
      const surveyQuestions = (survey.questions || []).filter(questionFilter);
      setQuestions(surveyQuestions);
    } catch (_e) {
      setError('Failed to load survey questions');
    } finally {
      setIsLoading(false);
    }
  }, [questionFilter]);

  // Load survey questions on open
  useEffect(() => {
    if (open && state === 'SURVEY' && questions.length === 0) {
      loadSurvey();
    }
  }, [open, state, questions.length, loadSurvey]);

  // If we have existing session, skip to generation
  useEffect(() => {
    if (open && existingSessionId && state === 'GENERATING') {
      triggerRegeneration(existingSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingSessionId, state]);

  const handleAnswerChange = useCallback(
    (value: string | string[]) => {
      if (!questions[currentStep]) return;
      setAnswers((prev) => ({
        ...prev,
        [questions[currentStep].id]: value,
      }));
    },
    [currentStep, questions]
  );

  const canGoNext = () => {
    const q = questions[currentStep];
    if (!q) return false;
    const answer = answers[q.id];
    if (q.required !== false) {
      return answer !== undefined && answer !== '' && (Array.isArray(answer) ? answer.length > 0 : true);
    }
    return true;
  };

  const handleSubmitSurvey = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Step 1: Create recovery session
      const session = await recoveryApi.createSession(userId, source);

      // Step 2: Submit survey responses
      const responses = Object.entries(answers).map(([questionId, answer]) => {
        const question = questions.find((q) => q.id === questionId);
        return {
          surveyId: 'SVY-five_onboarding_v1',
          questionId,
          questionText: question?.text || questionId,
          answer: Array.isArray(answer) ? answer.join(',') : answer,
          answerType: question?.type === 'MULTIPLE_CHOICE' ? 'MULTIPLE_CHOICE' : 'TEXT',
        };
      });

      await onboardingService.submitResponses(session.sessionId, responses);

      // Step 3: Trigger regeneration
      setState('GENERATING');
      await triggerRegeneration(session.sessionId);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to submit survey';
      setError(msg);
      setState('ERROR');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerRegeneration = async (sessionId: string) => {
    setIsLoading(true);
    try {
      const result = await recoveryApi.regenerate(userId, {
        sessionId,
        regenerateBio: true,
        regenerateLearningPath: true,
      });
      setCorrelationId(result.correlationId || null);

      // Poll for completion
      await pollForCompletion(result.correlationId || '');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Regeneration failed';
      setError(msg);
      setState('ERROR');
    } finally {
      setIsLoading(false);
    }
  };

  const pollForCompletion = async (corrId: string) => {
    const maxAttempts = 40; // ~7 minutes max (10s intervals) — agent can take 5min
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 10000));
      try {
        const status = await recoveryApi.getRegenerateStatus(userId, corrId);
        if (status.status === 'COMPLETED' || status.status === 'PARTIAL') {
          setState('COMPLETE');
          queryClient.invalidateQueries({ queryKey: recoveryKeys.all });
          queryClient.invalidateQueries({ queryKey: ['profiles'] });
          queryClient.invalidateQueries({ queryKey: ['learningPlan'] });
          toast({
            title: status.status === 'COMPLETED' ? 'Content Generated!' : 'Partially Generated',
            description: status.status === 'COMPLETED'
              ? 'Your bio and learning plan have been created.'
              : 'Some content was generated. The rest may take a bit longer.',
          });
          return;
        }
        if (status.status === 'FAILED') {
          setError(status.errorMessage || 'Generation failed. Please try again.');
          setState('ERROR');
          return;
        }
        // PROCESSING — keep polling
      } catch {
        // Ignore individual poll errors — service may be temporarily unavailable
      }
    }
    // Timeout — but check one more time if something was generated
    try {
      const finalStatus = await recoveryApi.getRegenerateStatus(userId, corrId);
      if (finalStatus.bioGenerated || finalStatus.learningPathGenerated) {
        setState('COMPLETE');
        queryClient.invalidateQueries({ queryKey: recoveryKeys.all });
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
        toast({
          title: 'Content Generated!',
          description: 'Your personalized content has been created.',
        });
        return;
      }
    } catch { /* ignore */ }
    setError('Generation is taking longer than expected. Please refresh the page in a few minutes.');
    setState('ERROR');
  };

  const handleClose = () => {
    if (state === 'GENERATING') return; // Don't close during generation
    onOpenChange(false);
    // Reset state for next open
    setTimeout(() => {
      setState(existingSessionId ? 'GENERATING' : 'SURVEY');
      setCurrentStep(0);
      setError(null);
      setCorrelationId(null);
    }, 300);
  };

  const progress = questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-500" />
            {state === 'SURVEY' && 'Quick Survey'}
            {state === 'GENERATING' && 'Generating Your Content'}
            {state === 'COMPLETE' && 'All Set!'}
            {state === 'ERROR' && 'Something Went Wrong'}
          </DialogTitle>
          <DialogDescription>
            {state === 'SURVEY' && 'Answer a few questions so we can create your personalized bio and learning plan.'}
            {state === 'GENERATING' && 'Our AI is creating your personalized bio and learning plan. This may take a minute...'}
            {state === 'COMPLETE' && 'Your bio and learning plan have been generated successfully.'}
            {state === 'ERROR' && (error || 'An error occurred during generation.')}
          </DialogDescription>
        </DialogHeader>

        {/* Survey State */}
        {state === 'SURVEY' && questions.length > 0 && (
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Question {currentStep + 1} of {questions.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-1.5" />
            </div>

            <QuestionCard
              question={questions[currentStep]}
              value={answers[questions[currentStep]?.id]}
              onChange={handleAnswerChange}
            />

            <div className="flex justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>

              {currentStep < questions.length - 1 ? (
                <Button
                  size="sm"
                  onClick={() => setCurrentStep((s) => s + 1)}
                  disabled={!canGoNext()}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleSubmitSurvey}
                  disabled={!canGoNext() || isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Generate
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Loading survey */}
        {state === 'SURVEY' && questions.length === 0 && isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
          </div>
        )}

        {/* Generating State */}
        {state === 'GENERATING' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="h-16 w-16 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Creating your personalized bio and learning plan using AI. This typically takes 30-60 seconds...
            </p>
          </div>
        )}

        {/* Complete State */}
        {state === 'COMPLETE' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Your profile is now complete. Refresh the page to see your personalized content.
            </p>
            <Button onClick={handleClose} size="sm">
              Done
            </Button>
          </div>
        )}

        {/* Error State */}
        {state === 'ERROR' && (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <p className="text-sm text-red-600 dark:text-red-400 text-center">
              {error}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleClose}>
                Close
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setError(null);
                  setState('SURVEY');
                  setCurrentStep(0);
                }}
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
